import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import ScrollToTop from "../shared/ScrollToTop";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, Building2, CalendarCheck, ListChecks, Image, FileText, Bot, Settings, LogOut, Handshake, Link2, Globe, Book, Mic, ShieldCheck, BarChart3, Menu, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { IconOnly } from "../brand";

const navGroups = [
  {
    label: "Operations",
    items: [
      { to: "/altura-command/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/altura-command/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/altura-command/leads", label: "CRM", icon: Users },
      { to: "/altura-command/opportunities", label: "Opportunities", icon: Building2 },
      { to: "/altura-command/consultations", label: "Consultations", icon: CalendarCheck },
      { to: "/altura-command/tasks", label: "Tasks", icon: ListChecks },
    ],
  },
  {
    label: "Relationships",
    items: [
      { to: "/altura-command/partners", label: "Strategic Partnerships", icon: Handshake },
      { to: "/altura-command/global-link", label: "Global Link", icon: Link2 },
    ],
  },
  {
    label: "Knowledge",
    items: [
      { to: "/altura-command/country-intel", label: "Country Intelligence", icon: Globe },
      { to: "/altura-command/content", label: "Learning Center", icon: Book },
      { to: "/altura-command/resources", label: "Resources", icon: FileText },
      { to: "/altura-command/media", label: "Media Library", icon: Image },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/altura-command/auri", label: "AURI", icon: Bot },
      { to: "/altura-command/voice", label: "Voice AURI", icon: Mic },
      { to: "/altura-command/security", label: "Security Log", icon: ShieldCheck },
      { to: "/altura-command/settings", label: "Settings", icon: Settings },
    ],
  },
];

export default function AdminShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/altura-command/login", { replace: true });
  }, [user, loading, navigate]);

  // Close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-altura-ivory">
        <div className="text-altura-espresso/60 text-sm">Loading Altura Command Center…</div>
      </div>
    );
  }

  const SidebarInner = (
    <>
      <div className="px-5 pt-5 pb-3 lg:pt-6 lg:pb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <IconOnly size={32} theme="on-dark" />
          <div>
            <div className="font-editorial text-lg leading-none text-altura-ivory">Altura</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-altura-gold">Command Center</div>
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-altura-ivory/70 hover:text-altura-ivory p-1"
          aria-label="Close menu"
          data-testid="admin-mobile-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="hidden lg:block px-5 pb-4 border-b border-altura-ivory/10">
        <div className="text-[10px] tracking-[0.2em] uppercase text-altura-ivory/40">Executive Operations Platform</div>
      </div>
      <div className="lg:hidden mx-5 border-b border-altura-ivory/10" />
      <nav className="flex-1 overflow-y-auto py-3 lg:py-4 px-3 space-y-2 lg:space-y-4">
        {navGroups.map((g) => (
          <div key={g.label}>
            <div className="px-3 py-1 lg:py-1.5 text-[9px] tracking-[0.24em] uppercase text-altura-ivory/35">{g.label}</div>
            <div className="space-y-0.5">
              {g.items.map((n) => (
                <NavLink key={n.to} to={n.to}
                         className={({ isActive }) => `admin-nav-item ${isActive ? "admin-nav-item-active" : ""}`}
                         data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <n.icon className="h-4 w-4" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 lg:p-4 border-t border-altura-ivory/10">
        <div className="mb-2 lg:mb-3 text-xs text-altura-ivory/60">
          <div className="text-altura-ivory truncate">{user.name}</div>
          <div className="truncate text-[11px]">{user.email}</div>
        </div>
        <button onClick={logout} className="flex w-full items-center gap-2 rounded-md border border-altura-ivory/15 px-3 py-2 text-xs text-altura-ivory hover:border-altura-gold hover:text-altura-gold transition" data-testid="admin-logout">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="admin-body flex min-h-screen" data-testid="admin-shell">
      {/* Desktop sidebar — always visible on lg+ */}
      <aside className="admin-sidebar sticky top-0 hidden lg:flex h-screen w-64 flex-col">
        {SidebarInner}
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-altura-espresso/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-mobile-backdrop"
        />
      )}
      <aside
        className={`admin-sidebar lg:hidden fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col transform transition-transform duration-300 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ height: "100dvh" }}
        data-testid="admin-mobile-sidebar"
        aria-hidden={!mobileOpen}
      >
        {SidebarInner}
      </aside>

      <div className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-altura-espresso text-altura-ivory px-4 py-3 border-b border-altura-ivory/10">
          <button
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center gap-2 text-altura-ivory"
            aria-label="Open menu"
            data-testid="admin-mobile-open"
          >
            <Menu className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.2em]">Menu</span>
          </button>
          <div className="flex items-center gap-2">
            <IconOnly size={22} theme="on-dark" />
            <div className="font-editorial text-sm text-altura-ivory">Altura</div>
          </div>
        </div>
        <Outlet />
        <ScrollToTop />
      </div>
    </div>
  );
}
